import { Component, ElementRef, forwardRef, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IconComponent, InputComponent, InputSearchComponent, ISelect } from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { countryFlagImageUrl } from '../../utils/country-flag.util';
import {
	findPrefixOptionByDialCode,
	getPrefixCountries,
	getPrefixOptionCountryName,
	getPrefixOptionDialCode,
	getPrefixOptionIso2,
	prefixCountryTranslationKey,
	PREFIX_COUNTRY_I18N_PREFIX,
	resolveDialCodeFromPrefixValue,
	resolvePrefixOption,
} from '../../utils/prefix-options.util';
import { normalizeNationalNumber, parseE164, toE164 } from '../../utils/phone.util';
import { phonePartsValidator } from '../../validators/phone-e164.validator';

@Component({
	selector: 'app-phone-input',
	imports: [ReactiveFormsModule, InputComponent, InputSearchComponent, IconComponent, TranslatePipe],
	templateUrl: './phone-input.component.html',
	styleUrl: './phone-input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PhoneInputComponent),
			multi: true,
		},
	],
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() hasError = false;
	@Input() disabled = false;
	@Input() defaultPrefix = '+34';
	/** Prefijo de clave i18n para nombres de país; por defecto `MASTER.COUNTRIES`. */
	@Input() countryI18nPrefix = PREFIX_COUNTRY_I18N_PREFIX;

	private readonly translate = inject(TranslateService);
	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly subscriptions = new Subscription();
	private syncingExternalValue = false;

	public internalForm = new FormGroup(
		{
			prefix: new FormControl<string>('', { nonNullable: true }),
			number: new FormControl<string>('', { nonNullable: true }),
		},
		{ validators: phonePartsValidator() },
	);

	public prefixOptions: ISelect[] = [];
	public filteredPrefixOptions: ISelect[] = [];
	public prefixMenuOpen = false;
	public prefixSearch = new FormControl<string>('', { nonNullable: true });
	public readonly countryFlagImageUrl = countryFlagImageUrl;

	private onChange: (value: string | null) => void = () => undefined;
	public onTouched: () => void = () => undefined;

	ngOnInit(): void {
		this.refreshPrefixOptions();
		this.applyDefaultPrefix();
		this.filteredPrefixOptions = this.prefixOptions;
		this.subscriptions.add(
			this.internalForm.valueChanges.subscribe(() => {
				if (this.syncingExternalValue) {
					return;
				}
				this.emitValue();
			}),
		);
		this.subscriptions.add(
			this.translate.onLangChange.subscribe(() => {
				this.syncPrefixLabelsOnLangChange();
			}),
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.prefixMenuOpen) {
			return;
		}
		const target = event.target;
		if (!(target instanceof Node)) {
			return;
		}
		if (!this.elementRef.nativeElement.contains(target)) {
			this.closePrefixMenu();
		}
	}

	@HostListener('document:keydown.escape')
	onEscapeKey(): void {
		this.closePrefixMenu();
	}

	writeValue(value: string | null): void {
		this.syncingExternalValue = true;
		const parsed = parseE164(value);
		if (parsed) {
			const option = findPrefixOptionByDialCode(parsed.prefix, this.prefixOptions);
			this.internalForm.patchValue(
				{
					prefix: option ? String(option.id) : parsed.prefix,
					number: parsed.number,
				},
				{ emitEvent: false },
			);
		} else if (!value) {
			const defaultOption = findPrefixOptionByDialCode(this.defaultPrefix, this.prefixOptions);
			this.internalForm.patchValue(
				{
					prefix: defaultOption ? String(defaultOption.id) : this.defaultPrefix,
					number: '',
				},
				{ emitEvent: false },
			);
		}
		this.syncingExternalValue = false;
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	/**
	 * El padre marca touched en submit (`markAllAsTouched` / botón Generar link).
	 * No llamar onTouched en blur/focusout: evita errores rojos al elegir prefijo.
	 */
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.closePrefixMenu();
			this.internalForm.disable({ emitEvent: false });
			return;
		}
		this.internalForm.enable({ emitEvent: false });
	}

	/** Cierra el menú de prefijo al enfocar el número (QA NIF-520). */
	public onNumberFocus(): void {
		this.closePrefixMenu();
	}

	public togglePrefixMenu(event: MouseEvent): void {
		event.stopPropagation();
		if (this.disabled) {
			return;
		}
		this.prefixMenuOpen = !this.prefixMenuOpen;
		if (this.prefixMenuOpen) {
			this.prefixSearch.setValue('', { emitEvent: false });
			this.filteredPrefixOptions = this.prefixOptions;
		}
	}

	public selectPrefix(option: ISelect, event: MouseEvent): void {
		event.stopPropagation();
		this.internalForm.patchValue({ prefix: String(option.id) });
		this.closePrefixMenu();
	}

	/**
	 * Mismo patrón que `tec-select`: filtrado inmediato vía `(searchInput)` / `(input)`.
	 * @see https://infinite-ux.tecalis.com/tecalis/design-system/int/docs-static/Select.md
	 */
	public onPrefixSearchInput(event: Event | string): void {
		const inputValue = typeof event === 'string' ? event : (event.target as HTMLInputElement | null)?.value;
		this.filterPrefixOptions(inputValue ?? this.prefixSearch.value ?? '');
	}

	public filterPrefixOptions(term: string): void {
		const normalized = term.trim().toLowerCase();
		if (!normalized) {
			this.filteredPrefixOptions = this.prefixOptions;
			return;
		}
		this.filteredPrefixOptions = this.prefixOptions.filter((option) => {
			const countryName = getPrefixOptionCountryName(option).toLowerCase();
			const dialCode = getPrefixOptionDialCode(option).toLowerCase();
			const iso2 = getPrefixOptionIso2(option).toLowerCase();
			return countryName.startsWith(normalized) || dialCode.startsWith(normalized) || iso2.startsWith(normalized);
		});
	}

	public selectedDialCode(): string {
		return (
			resolveDialCodeFromPrefixValue(this.internalForm.get('prefix')?.value, this.prefixOptions) ||
			this.defaultPrefix
		);
	}

	public selectedPrefixId(): string {
		const option = resolvePrefixOption(this.internalForm.get('prefix')?.value, this.prefixOptions);
		return option ? String(option.id) : '';
	}

	public selectedPrefixIso2(): string {
		return getPrefixOptionIso2(resolvePrefixOption(this.internalForm.get('prefix')?.value, this.prefixOptions));
	}

	public getPrefixIso2(option: ISelect): string {
		return getPrefixOptionIso2(option);
	}

	public getPrefixCountryName(option: ISelect): string {
		return getPrefixOptionCountryName(option);
	}

	public getPrefixDialCode(option: ISelect): string {
		return getPrefixOptionDialCode(option);
	}

	private refreshPrefixOptions(): void {
		this.prefixOptions = getPrefixCountries({
			i18nPrefix: this.countryI18nPrefix,
			translateCountry: (iso3) => this.translateCountryName(iso3),
		});
		this.filteredPrefixOptions = this.prefixOptions;
	}

	private translateCountryName(iso3: string): string {
		const key = prefixCountryTranslationKey(iso3, this.countryI18nPrefix);
		const translated = this.translate.instant(key);
		return translated !== key ? translated : iso3;
	}

	private syncPrefixLabelsOnLangChange(): void {
		const currentPrefix = this.internalForm.get('prefix')?.value;
		this.refreshPrefixOptions();
		const resolved = resolvePrefixOption(currentPrefix, this.prefixOptions);
		if (resolved) {
			this.syncingExternalValue = true;
			this.internalForm.patchValue({ prefix: String(resolved.id) }, { emitEvent: false });
			this.syncingExternalValue = false;
		}
	}

	private applyDefaultPrefix(): void {
		if (!this.internalForm.get('prefix')?.value) {
			const defaultOption = findPrefixOptionByDialCode(this.defaultPrefix, this.prefixOptions);
			this.internalForm.patchValue(
				{ prefix: defaultOption ? String(defaultOption.id) : this.defaultPrefix },
				{ emitEvent: false },
			);
		}
	}

	private closePrefixMenu(): void {
		this.prefixMenuOpen = false;
		this.prefixSearch.setValue('', { emitEvent: false });
	}

	private emitValue(): void {
		const prefix = resolveDialCodeFromPrefixValue(this.internalForm.get('prefix')?.value, this.prefixOptions);
		const number = normalizeNationalNumber(this.internalForm.get('number')?.value);
		const e164 = toE164(prefix, number);
		this.onChange(e164);
	}
}
